import { useState, useMemo, useCallback } from 'react';
import {
  TaskSchedule,
  TaskConfiguration,
  TransferDirection,
  PostPickupAction,
  DeliveryMode,
} from '../../types/task';
import { Connection, HostingType, TransferMethod } from '../../types/connection';
import { FilterComponent } from '../shared/Filters/FilterComponent';
import { TaskScheduler } from './TaskScheduler';
import { connectionsApi } from '../../services/api/connections';
import { useAutoSuggestName } from '../../hooks/useAutoSuggestName';
import {
  getRelevantConfigEntries,
  configFieldLabel,
  pruneRemoteOnlyConfig,
  remotePathWording,
} from '../../utils/taskConfigDisplay';

export interface TaskCreatorProps {
  partners: Array<{ id: string; name: string }>;
  onSave: (taskData: {
    name: string;
    description?: string;  // THUB-160: optional free-form display label
    partnerId: string;
    connectionId: string;
    transferDirection: TransferDirection;  // FR-024: Inbound/Outbound direction
    schedule?: TaskSchedule;
    configuration: TaskConfiguration;
    archiveAccessEnabled?: boolean;  // FR-010: Read-only archive access
    // T090: Post-pickup action (Remote Pickup tasks)
    postPickupAction?: PostPickupAction;
    postPickupTargetPath?: string;
    // FR-042/043: Delivery mode (Remote Outbound tasks)
    deliveryMode?: DeliveryMode;
    deliverySchedule?: string;
    // 019: HTTP outbound task fields
    httpDeliveryMode?: 'standard' | 'with_metadata';
    httpResourcePath?: string;
    httpContentTypeOverride?: string;
    enableBase64?: boolean;
    pgpEnabled?: boolean;
  }) => void;
  onCancel: () => void;
  onValidateConfiguration?: (
    transferDirection: TransferDirection,
    config: TaskConfiguration
  ) => Promise<{ valid: boolean; errors: string[] }>;
  className?: string;
}

/**
 * T088: Transfer direction options derived from connection hosting type
 *
 * - Hosted connections: Partner connects to us
 *   - Inbound: Partner uploads files to us
 *   - Outbound: Partner downloads files from us
 *
 * - Remote connections: We connect to partner
 *   - Inbound: We pick up files from partner
 *   - Outbound: We drop files to partner
 */
interface TransferDirectionOption {
  label: string;
  value: TransferDirection;
  description: string;
}

// T088: Get transfer direction options based on connection hosting type and transfer method
const getTransferDirectionOptions = (
  hostingType: HostingType | undefined,
  transferMethod?: string
): TransferDirectionOption[] => {
  // For API (HTTP) connections - always remote
  if (transferMethod === 'api') {
    const w = remotePathWording('api');
    return [
      {
        label: 'Outbound',
        value: 'outbound',
        description: w.outboundDescription,
      },
      {
        label: 'Inbound',
        value: 'inbound',
        description: w.inboundDescription,
      },
    ];
  }

  if (hostingType === 'hosted') {
    // Hosted: Partner connects to our server
    return [
      {
        label: 'Inbound',
        value: 'inbound',
        description: 'Partner uploads files to our SFTP server',
      },
      {
        label: 'Outbound',
        value: 'outbound',
        description: 'Partner downloads files from our SFTP server',
      },
    ];
  } else if (hostingType === 'remote') {
    // Remote: We connect to partner's server. Wording is transport-aware
    // (SFTP directory vs Azure blob prefix vs HTTP resource path) -- THUB-220.
    const w = remotePathWording(transferMethod as TransferMethod | undefined);
    return [
      {
        label: 'Inbound',
        value: 'inbound',
        description: w.inboundDescription,
      },
      {
        label: 'Outbound',
        value: 'outbound',
        description: w.outboundDescription,
      },
    ];
  }
  // Default - show all options if no connection selected
  return [
    {
      label: 'Inbound',
      value: 'inbound',
      description: 'Receive files from partner',
    },
    {
      label: 'Outbound',
      value: 'outbound',
      description: 'Send files to partner',
    },
  ];
};

const defaultConfiguration: TaskConfiguration = {
  sourceDirectory: '',
  destinationDirectory: '',
  filePattern: '*',
  preserveTimestamp: true,
  deleteAfterTransfer: false,
  archiveProcessedFiles: false,
  validateFileFormat: false,
  notifyOnCompletion: false,
  notifyOnError: false,
};

const defaultSchedule: TaskSchedule = {
  type: 'manual',
  expression: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  enabled: true,
};

/**
 * T082: TaskCreator form with name field last and auto-suggest
 * T084: Auto-suggest preview in TaskForm
 * T086: "Use suggested name" button in TaskForm
 * T087: Filter connections by selected partner
 * T088: Direction dropdown derives behavior from connection hosting type
 */
export function TaskCreator({
  partners,
  onSave,
  onCancel,
  onValidateConfiguration,
  className = '',
}: TaskCreatorProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',  // THUB-160: free-form display label (optional)
    partnerId: '',
    connectionId: '',
    transferDirection: 'inbound' as TransferDirection,
  });

  const [schedule, setSchedule] = useState<TaskSchedule>(defaultSchedule);
  const [configuration, setConfiguration] =
    useState<TaskConfiguration>(defaultConfiguration);
  const [archiveAccessEnabled, setArchiveAccessEnabled] = useState(false);  // FR-010
  const [pgpEnabledRequested, setPgpEnabled] = useState(false);  // 021-pgp-encryption

  // T079: Auto-suggest name hook
  const { generateSuggestedName, hasRequiredContext } = useAutoSuggestName({ type: 'task' });

  // T090: Post-pickup action state (Remote Pickup tasks)
  const [postPickupAction, setPostPickupAction] = useState<PostPickupAction>('none');
  const [postPickupTargetPath, setPostPickupTargetPath] = useState<string>('');

  // FR-042/043: Delivery mode state (Remote Outbound tasks)
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('immediate');
  const [deliverySchedule, setDeliverySchedule] = useState<string>('');

  // 019: HTTP outbound task fields
  const [httpDeliveryMode, setHttpDeliveryMode] = useState<'standard' | 'with_metadata'>('standard');
  const [httpResourcePath, setHttpResourcePath] = useState('');
  const [httpContentTypeOverride, setHttpContentTypeOverride] = useState('');
  const [enableBase64, setEnableBase64] = useState(false);
  // T043: With Metadata mode fields
  const [httpResponseMode, setHttpResponseMode] = useState<'standard' | 'presigned_url'>('standard');
  const [requestBodyTemplate, setRequestBodyTemplate] = useState('');
  const [metadataFields, setMetadataFields] = useState<Array<{name: string; description: string; required: boolean}>>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // Clear specific field error when user modifies the field
  const clearFieldError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };
  const [currentStep, setCurrentStep] = useState<
    'basic' | 'schedule' | 'configuration' | 'review'
  >('basic');

  // Connection-related state
  const [availableConnections, setAvailableConnections] = useState<
    Connection[]
  >([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [connectionError, setConnectionError] = useState<string>('');

  // T087: Get selected connection for filtering and context
  const selectedConnection = useMemo(() => {
    return availableConnections.find(c => c.connectionId === formData.connectionId);
  }, [availableConnections, formData.connectionId]);

  // T088: Get hosting type from selected connection
  const selectedHostingType = useMemo((): HostingType | undefined => {
    if (!selectedConnection) return undefined;
    return selectedConnection.hostingType;
  }, [selectedConnection]);

  // T088: Get available transfer direction options based on connection hosting type and transfer method
  const transferDirectionOptions = useMemo(() => {
    return getTransferDirectionOptions(selectedHostingType, selectedConnection?.transferMethod);
  }, [selectedHostingType, selectedConnection?.transferMethod]);

  // T088: Get help text for the selected direction
  const directionHelpText = useMemo(() => {
    const option = transferDirectionOptions.find(o => o.value === formData.transferDirection);
    return option?.description || 'Select a transfer direction';
  }, [transferDirectionOptions, formData.transferDirection]);

  // T084: Generate suggested name based on current selections
  const suggestedName = useMemo(() => {
    return generateSuggestedName({
      connectionName: selectedConnection?.name,
      transferDirection: formData.transferDirection,
    });
  }, [generateSuggestedName, selectedConnection?.name, formData.transferDirection]);

  // Check if we have all required context for a suggestion
  const canSuggestName = useMemo(() => {
    return hasRequiredContext({
      connectionName: selectedConnection?.name,
      transferDirection: formData.transferDirection,
    });
  }, [hasRequiredContext, selectedConnection?.name, formData.transferDirection]);

  // THUB-203: PGP is only available when the selected connection has the key
  // required for this direction (public for outbound, private for inbound).
  const pgpKeyAvailable = useMemo(() => {
    if (!selectedConnection) return false;
    return formData.transferDirection === 'outbound'
      ? !!selectedConnection.pgpPublicKeyConfigured
      : !!selectedConnection.pgpPrivateKeyConfigured;
  }, [selectedConnection, formData.transferDirection]);

  // THUB-203 / THUB-298: never submit a hidden pgpEnabled -- DERIVED, not corrected afterwards.
  //
  // An effect used to force the toggle off once PGP became unavailable, i.e. one render LATER, so
  // a submit inside that window still sent a pgpEnabled the API rejects with PGP_KEY_MISSING --
  // the exact failure the effect was written to prevent. `pgpEnabledRequested` is what the
  // operator asked for; `pgpEnabled` is what we submit, and it requires the key to be available.
  // Same shape as TaskEditor's.
  const pgpEnabled = pgpEnabledRequested && pgpKeyAvailable;

  // THUB-298: hoisted above the effect that calls it and memoized (react-hooks/immutability --
  // the effect referenced it before its declaration). Unlike TaskEditor's version, this body also
  // reads `editingTask` for the auto-select branch, so that is a real dependency.
  const fetchConnectionsForPartner = useCallback(async (partnerId: string) => {
    if (!partnerId) {
      setAvailableConnections([]);
      setConnectionError('');
      return;
    }

    setLoadingConnections(true);
    setConnectionError('');

    try {
      const connections =
        await connectionsApi.getConnectionsByPartner(partnerId);
      setAvailableConnections(connections);

      if (connections.length === 0) {
        setConnectionError(
          'No connections found for this partner. Please create a connection first.'
        );
      } else {
        // Auto-select the only connection when none is chosen yet.
        // THUB-298: the "none chosen yet" test reads `prev` inside the updater rather than the
        // captured formData.connectionId -- that keeps this callback out of the formData
        // dependency chain AND avoids acting on a stale value if a connection was picked while
        // the request was in flight.
        if (connections.length === 1) {
          setFormData(prev =>
            prev.connectionId
              ? prev
              : { ...prev, connectionId: connections[0].connectionId }
          );
        }
      }
    } catch (error) {
      console.error('Failed to fetch connections:', error);
      setConnectionError('Failed to load connections for this partner.');
      setAvailableConnections([]);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  // THUB-298: the form-seeding effect that lived here is GONE, along with the edit mode it
  // served.
  //
  // It set thirteen useState values from an `editingTask` prop -- the single largest
  // set-state-in-effect in the codebase. Before converting it I checked who passes that prop:
  // nobody. TaskList's only <TaskCreator> is its CREATE modal, and editing goes through
  // TaskEditor, which is a separate component. No test passed it either. So the whole edit mode
  // was unreachable, and with it the effect, the 'Update Task' button label, and the
  // `!editingTask` guard on connection auto-select.
  //
  // Deriving thirteen values (or reaching for a key-based remount) would have been careful work on
  // a branch that cannot execute. Third time this ticket that a finding resolved to "delete" --
  // worth checking reachability BEFORE designing a fix.

  // Fetch connections when partner is selected

  // Handle partner selection change
  // T087: Filter connections by selected partner (connections reset on partner change)
  const handlePartnerChange = (partnerId: string) => {
    setFormData(prev => ({
      ...prev,
      partnerId,
      connectionId: '', // Reset connection when partner changes
      // Reset direction to default when connection is cleared
      transferDirection: 'inbound',
    }));
    clearFieldError('partnerId');
    clearFieldError('connectionId'); // Clear connection error since it's being reset

    if (partnerId) {
      fetchConnectionsForPartner(partnerId);
    } else {
      setAvailableConnections([]);
      setConnectionError('');
    }
  };

  // T088: Handle connection selection and reset direction to match hosting type
  const handleConnectionChange = (connectionId: string) => {
    const connection = availableConnections.find(c => c.connectionId === connectionId);
    const hostingType = connection?.hostingType;
    const transferMethod = connection?.transferMethod;
    const options = getTransferDirectionOptions(hostingType, transferMethod);

    // Default to first available direction option for this hosting type
    const defaultDirection = options[0]?.value || 'inbound';

    setFormData(prev => ({
      ...prev,
      connectionId,
      transferDirection: defaultDirection,
    }));
    clearFieldError('connectionId');
  };

  // T088: Handle direction selection
  const handleDirectionChange = (direction: TransferDirection) => {
    setFormData(prev => ({
      ...prev,
      transferDirection: direction,
    }));
    clearFieldError('transferDirection');
  };

  // T086: Handle "Use suggested name" button click
  const handleUseSuggestedName = () => {
    if (canSuggestName && suggestedName) {
      setFormData(prev => ({
        ...prev,
        name: suggestedName
      }));
      // Clear any name error
      if (errors.name) {
        setErrors(prev => ({ ...prev, name: '' }));
      }
    }
  };

  const validateBasicInfo = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Task name is required';
    }

    if (!formData.partnerId) {
      newErrors.partnerId = 'Partner selection is required';
    }

    if (!formData.connectionId) {
      newErrors.connectionId = 'Connection selection is required';
    }

    if (!formData.transferDirection) {
      newErrors.transferDirection = 'Transfer direction is required';
    }

    // Validate that selected connection exists and belongs to selected partner
    if (formData.connectionId && formData.partnerId) {
      const selectedConnection = availableConnections.find(
        conn => conn.connectionId === formData.connectionId
      );
      if (!selectedConnection) {
        newErrors.connectionId =
          'Selected connection is not valid for this partner';
      } else if (selectedConnection.partnerId !== formData.partnerId) {
        newErrors.connectionId =
          'Selected connection does not belong to the selected partner';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateConfiguration = async (): Promise<boolean> => {
    if (!onValidateConfiguration) return true;

    setIsValidating(true);
    try {
      const result = await onValidateConfiguration(
        formData.transferDirection,
        configuration
      );
      setValidationErrors(result.errors);
      return result.valid;
    } catch (error) {
      console.warn(
        'Configuration validation failed (likely CORS issue):',
        error
      );
      // Don't block task creation if validation fails due to CORS/network issues
      setValidationErrors([
        'Configuration validation unavailable - proceeding without validation',
      ]);
      return true; // Allow creation to proceed
    } finally {
      setIsValidating(false);
    }
  };

  // Determine if the current configuration requires scheduling
  // Scheduling is available for inbound tasks on remote connections (pickup scenarios)
  const requiresScheduling = (): boolean => {
    // Remote inbound (pickup) tasks require scheduling to poll for files
    return selectedHostingType === 'remote' && formData.transferDirection === 'inbound';
  };

  const getNextStep = (currentStep: string): string => {
    const hasScheduling = requiresScheduling();

    switch (currentStep) {
      case 'basic':
        return hasScheduling ? 'schedule' : 'configuration';
      case 'schedule':
        return 'configuration';
      case 'configuration':
        return 'review';
      default:
        return 'basic';
    }
  };

  const getPreviousStep = (currentStep: string): string => {
    const hasScheduling = requiresScheduling();

    switch (currentStep) {
      case 'schedule':
        return 'basic';
      case 'configuration':
        return hasScheduling ? 'schedule' : 'basic';
      case 'review':
        return 'configuration';
      default:
        return 'basic';
    }
  };

  const handleNext = async () => {
    switch (currentStep) {
      case 'basic':
        if (validateBasicInfo()) {
          const nextStep = getNextStep(currentStep);
          setCurrentStep(nextStep as any);

          // If skipping schedule, set to manual
          if (
            nextStep === 'configuration' &&
            !requiresScheduling()
          ) {
            setSchedule({ ...defaultSchedule, type: 'manual' });
          }
        }
        break;
      case 'schedule':
        setCurrentStep(getNextStep(currentStep) as any);
        break;
      case 'configuration': {
        const isValid = await validateConfiguration();
        if (isValid) {
          setCurrentStep(getNextStep(currentStep) as any);
        }
        break;
      }
      case 'review':
        handleSave();
        break;
    }
  };

  const handlePrevious = () => {
    const previousStep = getPreviousStep(currentStep);
    setCurrentStep(previousStep as any);
  };

  // T043: Metadata field management functions
  const addMetadataField = () => {
    setMetadataFields(prev => [...prev, { name: '', description: '', required: false }]);
  };

  const removeMetadataField = (index: number) => {
    setMetadataFields(prev => prev.filter((_, i) => i !== index));
  };

  const updateMetadataField = (index: number, field: 'name' | 'description' | 'required', value: string | boolean) => {
    setMetadataFields(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const handleSave = () => {
    // FR-023: Determine if this is a Remote connection
    const selectedConn = availableConnections.find(
      conn => conn.connectionId === formData.connectionId
    );
    const isRemoteConnection = selectedConn?.hostingType === 'remote';
    const isHttpConnection = selectedConn?.transferMethod === 'api';
    // T088: Pickup = Remote + Inbound, Drop = Remote + Outbound
    const isPickupTask = isRemoteConnection && formData.transferDirection === 'inbound';
    const isDropTask = isRemoteConnection && formData.transferDirection === 'outbound';
    const isHttpOutbound = isHttpConnection && formData.transferDirection === 'outbound';
    const isHttpInbound = isHttpConnection && formData.transferDirection === 'inbound';

    onSave({
      name: formData.name,
      // THUB-160: send the display label only when non-empty (trim -> undefined),
      // mirroring the Partner description pattern so we never persist a blank string.
      description: formData.description.trim() || undefined,
      partnerId: formData.partnerId,
      connectionId: formData.connectionId,
      transferDirection: formData.transferDirection,
      // Schedule only applies to Remote+Inbound (pickup) tasks
      ...(isPickupTask && { schedule }),
      // THUB-163: drop remote-only path keys (sourceDirectory/destinationDirectory)
      // for non-remote connections so hosted/HTTP tasks don't persist empty values.
      configuration: pruneRemoteOnlyConfig(
        configuration,
        selectedConn?.hostingType
      ),
      archiveAccessEnabled,  // FR-010
      pgpEnabled,  // 021-pgp-encryption
      // T090: Include post-pickup action only for Remote Pickup tasks
      ...(isPickupTask && {
        postPickupAction,
        postPickupTargetPath: postPickupAction === 'move' ? postPickupTargetPath : undefined,
      }),
      // T091: Include trigger type only for Remote Drop tasks.
      // THUB-220: coerce to immediate for non-SFTP methods -- scheduled batch delivery is
      // SFTP-only, and the option is hidden for other methods, but a stale 'scheduled' state
      // (from switching connection after selecting it) must not leak into the request.
      ...(isDropTask && {
        deliveryMode: supportsScheduledDelivery() ? deliveryMode : 'immediate',
        deliverySchedule: (supportsScheduledDelivery() && deliveryMode === 'scheduled')
          ? deliverySchedule : undefined,
      }),
      // 019: Include HTTP fields only for HTTP outbound tasks
      ...(isHttpOutbound && {
        httpDeliveryMode,
        ...(httpResourcePath && { httpResourcePath }),
        ...(httpContentTypeOverride && { httpContentTypeOverride }),
        enableBase64,
        // T043: Include With Metadata fields when with_metadata mode is selected
        ...(httpDeliveryMode === 'with_metadata' && {
          requestBodyTemplate,
          metadataFields,
        }),
      }),
      // T050/T052: Include HTTP inbound fields
      ...(isHttpInbound && {
        httpResponseMode,
        ...(httpResourcePath && { httpResourcePath }),
      }),
    });
  };

  const handleConfigurationChange = (
    key: keyof TaskConfiguration,
    value: any
  ) => {
    setConfiguration(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // FR-010/FR-023: Check if archive access can be enabled (only for hosted connections)
  // Hosted = partner connects to our SFTP server
  const isHostedConnection = (): boolean => {
    return selectedConnection?.hostingType === 'hosted';
  };

  // T090/FR-023: Check if this is a Remote Pickup task
  // Remote = we connect to partner's server, Inbound = data flows to us
  const isRemotePickupTask = (): boolean => {
    return selectedConnection?.hostingType === 'remote' && formData.transferDirection === 'inbound';
  };

  // T091/FR-023: Check if this is a Remote Drop task
  // Remote = we connect to partner's server, Outbound = data flows to them
  const isRemoteDropTask = (): boolean => {
    return selectedConnection?.hostingType === 'remote' && formData.transferDirection === 'outbound';
  };

  // THUB-220: scheduled (batch) delivery is only wired for SFTP remote outbound. For
  // azure_blob (and other non-SFTP methods) file_processing always delivers immediately, so
  // offering "scheduled" would silently degrade to immediate + an inert schedule. Only expose
  // the scheduled option for SFTP; the backend rejects it for other methods as a backstop.
  const supportsScheduledDelivery = (): boolean => {
    return selectedConnection?.transferMethod === 'sftp';
  };
  const deliveryModeOptions = (supportsScheduledDelivery()
    ? ['immediate', 'scheduled']
    : ['immediate']) as ReadonlyArray<DeliveryMode>;

  // 019: Check if this is an HTTP outbound task
  const isHttpOutboundTask = (): boolean => {
    return selectedConnection?.transferMethod === 'api' && formData.transferDirection === 'outbound';
  };

  // 019: Check if this is an HTTP inbound task
  const isHttpInboundTask = (): boolean => {
    return selectedConnection?.transferMethod === 'api' && formData.transferDirection === 'inbound';
  };

  /**
   * T082: renderBasicInfo with name field LAST
   * T087: Connection dropdown filtered by selected partner
   * T088: Direction dropdown derives options from connection hosting type
   */
  const renderBasicInfo = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-5 pb-2 border-b border-neutral-200 dark:border-neutral-700">
        Task Configuration
      </h3>
      <div className="grid grid-cols-1 gap-5">
        {/* T087: Partner selection FIRST - triggers connection filtering */}
        <FilterComponent
          field="partnerId"
          label="Partner"
          type="select"
          options={partners.map(partner => ({
            label: partner.name,
            value: partner.id,
          }))}
          value={formData.partnerId}
          onChange={filter => handlePartnerChange(filter.value)}
          placeholder="Select a partner"
          required
        />
        {errors.partnerId && (
          <span className="text-danger-500 text-xs mt-1">{errors.partnerId}</span>
        )}

        {/* T087: Connection Selection - Filtered by selected partner */}
        {formData.partnerId && (
          <div className="space-y-2">
            <label htmlFor="connectionId" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Connection *
            </label>
            {loadingConnections ? (
              <div className="p-3 text-center italic text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-md">
                <span>Loading connections...</span>
              </div>
            ) : connectionError ? (
              <div className="flex items-center justify-between gap-3 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-md">
                <span className="text-danger-600 dark:text-danger-400 text-sm">{connectionError}</span>
                <button
                  type="button"
                  className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white border-none rounded text-xs cursor-pointer transition-colors duration-200"
                  onClick={() => fetchConnectionsForPartner(formData.partnerId)}
                >
                  Retry
                </button>
              </div>
            ) : (
              <>
                <select
                  id="connectionId"
                  value={formData.connectionId}
                  onChange={e => handleConnectionChange(e.target.value)}
                  className={`
                    w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200
                    bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200
                    ${errors.connectionId ? 'border-danger-500' : 'border-neutral-300 dark:border-neutral-600'}
                    focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                    disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:cursor-not-allowed
                  `}
                  disabled={availableConnections.length === 0}
                >
                  <option value="">Select a connection</option>
                  {availableConnections.map(connection => (
                    <option
                      key={connection.connectionId}
                      value={connection.connectionId}
                    >
                      {connection.name} ({connection.hostingType} / {connection.transferMethod.toUpperCase()})
                    </option>
                  ))}
                </select>
                {availableConnections.length > 0 && (
                  <div className="mt-1.5">
                    <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                      {availableConnections.length} connection
                      {availableConnections.length !== 1 ? 's' : ''} available
                    </small>
                  </div>
                )}
              </>
            )}
            {errors.connectionId && (
              <span className="text-danger-500 text-xs mt-1">{errors.connectionId}</span>
            )}
          </div>
        )}

        {/* T088: Transfer Direction - Options derived from connection hosting type */}
        {formData.connectionId && (
          <div className="space-y-2">
            <label htmlFor="transferDirection" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Transfer Direction *
            </label>
            <select
              id="transferDirection"
              value={formData.transferDirection}
              onChange={e => handleDirectionChange(e.target.value as TransferDirection)}
              className={`
                w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200
                bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200
                ${errors.transferDirection ? 'border-danger-500' : 'border-neutral-300 dark:border-neutral-600'}
                focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
              `}
            >
              {transferDirectionOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.transferDirection && (
              <span className="text-danger-500 text-xs mt-1">{errors.transferDirection}</span>
            )}

            {/* T088: Help text showing what this direction means for the selected connection */}
            <div className="mt-1.5">
              <small className="text-neutral-500 dark:text-neutral-400 text-xs">{directionHelpText}</small>
            </div>
          </div>
        )}
      </div>

      {formData.transferDirection && (
        <div className="mt-5 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md">
          {requiresScheduling() ? (
            <p className="m-0 text-sm text-primary-700 dark:text-primary-400">
              <strong>Scheduled Transfer:</strong> This transfer direction supports
              automatic scheduling to check for new files.
            </p>
          ) : (
            <p className="m-0 text-sm text-primary-700 dark:text-primary-400">
              <strong>Manual Transfer:</strong> This transfer direction is executed
              on-demand and does not require scheduling.
            </p>
          )}
        </div>
      )}

      {/* T082/T084/T086: Task Name field LAST with auto-suggest */}
      {formData.connectionId && formData.transferDirection && (
        <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">
            Task Name
          </h4>

          {/* T084: Auto-suggest preview */}
          {canSuggestName && suggestedName && (
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-md">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Suggested name:</span>
              <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{suggestedName}</span>
              {/* T086: "Use suggested name" button */}
              <button
                type="button"
                className="
                  px-3 py-1.5 bg-success-500 hover:bg-success-600
                  text-white border-none rounded text-xs cursor-pointer
                  transition-colors duration-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
                onClick={handleUseSuggestedName}
                disabled={formData.name === suggestedName}
              >
                Use suggested
              </button>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="taskName" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Task Name *
            </label>
            <input
              id="taskName"
              type="text"
              value={formData.name}
              onChange={e => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
                clearFieldError('name');
              }}
              className={`
                w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200
                bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200
                ${errors.name ? 'border-danger-500' : 'border-neutral-300 dark:border-neutral-600'}
                focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
              `}
              placeholder={canSuggestName ? suggestedName : 'Enter a descriptive task name'}
            />
            {errors.name && <span className="text-danger-500 text-xs mt-1 block">{errors.name}</span>}
            <small className="text-neutral-500 dark:text-neutral-400 text-xs block mt-1.5">
              Choose a descriptive name for this task.
              {canSuggestName && ' You can use the suggested name above or enter a custom one.'}
              {' '}This name is <strong>permanent</strong> and determines the upload path for partners (e.g., <code className="text-xs">inbound/{'{name}'}/</code>).
              It cannot be changed after creation.
            </small>
          </div>

          {/* THUB-160: optional free-form display label. Unlike the immutable name
              above, this can be edited any time and is what users read in lists and
              details. Kept separate so the S3 prefix stays auto-derived from name. */}
          <div className="mt-5 space-y-2">
            <label htmlFor="taskDescription" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Display Name / Description
            </label>
            <textarea
              id="taskDescription"
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter a friendly description (optional)"
              rows={3}
              maxLength={500}
              className="
                w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200
                bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200
                border-neutral-300 dark:border-neutral-600
                placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20
                resize-none
              "
            />
            <small className="text-neutral-500 dark:text-neutral-400 text-xs block">
              A human-readable label shown across the UI. The permanent task name above stays the upload path.
              {' '}{formData.description.length}/500 characters
            </small>
          </div>
        </div>
      )}
    </div>
  );

  const renderSchedule = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-5 pb-2 border-b border-neutral-200 dark:border-neutral-700">
        Schedule Configuration
      </h3>
      <TaskScheduler schedule={schedule} onChange={setSchedule} />
    </div>
  );

  const renderConfiguration = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-5 pb-2 border-b border-neutral-200 dark:border-neutral-700">
        Transfer Configuration
      </h3>

      {validationErrors.length > 0 && (
        <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-md p-4 mb-5">
          <h4 className="text-danger-600 dark:text-danger-400 text-sm font-semibold m-0 mb-2">Configuration Errors:</h4>
          <ul className="m-0 pl-5 text-danger-600 dark:text-danger-400 text-sm">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-6">
        {/* Remote path - only for remote connections. Wording is transport-aware
            (SFTP directory / Azure blob prefix / HTTP resource path) -- THUB-220. */}
        {selectedHostingType === 'remote' && (() => {
          const pathWording = remotePathWording(selectedConnection?.transferMethod);
          return (
          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">{pathWording.sectionHeading}</h4>
            {formData.transferDirection === 'inbound' ? (
              <div className="space-y-2">
                <label htmlFor="sourceDirectory" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {pathWording.sourceLabel}
                </label>
                <input
                  id="sourceDirectory"
                  type="text"
                  value={configuration.sourceDirectory || ''}
                  onChange={e => handleConfigurationChange('sourceDirectory', e.target.value)}
                  placeholder="/outbound or /data/exports"
                  className="w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                />
                <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                  {pathWording.sourceHelp}
                </small>
              </div>
            ) : (
              <div className="space-y-2">
                <label htmlFor="destinationDirectory" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {pathWording.destinationLabel}
                </label>
                <input
                  id="destinationDirectory"
                  type="text"
                  value={configuration.destinationDirectory || ''}
                  onChange={e => handleConfigurationChange('destinationDirectory', e.target.value)}
                  placeholder="/inbound or /data/imports"
                  className="w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                />
                <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                  {pathWording.destinationHelp}
                </small>
              </div>
            )}
          </div>
          );
        })()}

        {/* Hosted path info - explain auto-managed paths */}
        {selectedHostingType === 'hosted' && (
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md p-5">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-2">File Paths</h4>
            <p className="m-0 text-sm text-neutral-600 dark:text-neutral-400">
              File paths are automatically managed based on the task prefix.
              {formData.transferDirection === 'inbound'
                ? ' Partners upload files to the inbound directory on our SFTP server.'
                : ' Value stream teams drop files to the staging directory, and partners download from the outbound directory.'}
            </p>
          </div>
        )}

        <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5">
          <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">File Handling</h4>
          <div className="space-y-2">
            <label htmlFor="filePattern" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              File Pattern
            </label>
            <input
              id="filePattern"
              type="text"
              value={configuration.filePattern || '*'}
              onChange={e => handleConfigurationChange('filePattern', e.target.value)}
              placeholder="*.txt, *.csv, etc."
              className="w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            />
            <small className="text-neutral-500 dark:text-neutral-400 text-xs">
              Glob pattern to match files for processing. Use * to match all files.
            </small>
          </div>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5">
          <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">Notifications</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700 dark:text-neutral-300">
              <input type="checkbox" checked={configuration.notifyOnCompletion} onChange={e => handleConfigurationChange('notifyOnCompletion', e.target.checked)} className="w-4 h-4 accent-primary-500" />
              Notify on completion
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700 dark:text-neutral-300">
              <input type="checkbox" checked={configuration.notifyOnError} onChange={e => handleConfigurationChange('notifyOnError', e.target.checked)} className="w-4 h-4 accent-primary-500" />
              Notify on error
            </label>
          </div>
        </div>

        {/* FR-010: Archive Access Section - Only show for hosted connections */}
        {isHostedConnection() && (
          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5 border-l-[3px] border-l-primary-500">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">Archive Access</h4>
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700 dark:text-neutral-300">
                <input type="checkbox" checked={archiveAccessEnabled} onChange={e => setArchiveAccessEnabled(e.target.checked)} className="w-4 h-4 accent-primary-500" />
                <span>Enable partner read-only archive access</span>
              </label>
              <div className="mt-2 p-3 bg-neutral-100 dark:bg-neutral-700 rounded text-xs text-neutral-500 dark:text-neutral-400">
                When enabled, the partner will have read-only access to their
                archived files in the <code className="bg-neutral-200 dark:bg-neutral-600 px-1 rounded">/archive/{formData.transferDirection}/[task]/</code> directory.
                This allows partners to review their historical file transfers.
              </div>
            </div>
          </div>
        )}

        {/* 021 / THUB-203: PGP Encryption - shown only when the selected connection
            has the PGP key required for the task's direction (public for outbound,
            private for inbound). If no matching key is configured, PGP cannot work
            for this task, so we don't offer it. */}
        {pgpKeyAvailable && (
          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5 border-l-[3px] border-l-primary-500">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">PGP Encryption</h4>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pgpEnabled}
                  onChange={e => setPgpEnabled(e.target.checked)}
                  className="w-4 h-4 accent-primary-500"
                />
                <span>Enable PGP encryption for file transfers</span>
              </label>
            </div>
          </div>
        )}

        {/* T090: Post-Pickup Action Section - Only show for Remote Pickup tasks */}
        {isRemotePickupTask() && (
          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5 border-l-[3px] border-l-primary-500">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-2">Post-Pickup Action</h4>
            <p className="m-0 mb-4 text-neutral-500 dark:text-neutral-400 text-sm">
              Choose what happens to files on the partner server after they are successfully picked up.
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="postPickupAction" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Action After Pickup
                </label>
                <select
                  id="postPickupAction"
                  value={postPickupAction}
                  onChange={e => setPostPickupAction(e.target.value as PostPickupAction)}
                  className="w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="none">None - Leave file on partner server</option>
                  <option value="delete">Delete - Remove file after pickup</option>
                  <option value="move">Move - Move file to archive folder</option>
                </select>
              </div>

              {postPickupAction === 'move' && (
                <div className="space-y-2">
                  <label htmlFor="postPickupTargetPath" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Archive Folder Path
                  </label>
                  <input
                    id="postPickupTargetPath"
                    type="text"
                    value={postPickupTargetPath}
                    onChange={e => setPostPickupTargetPath(e.target.value)}
                    placeholder="/archive"
                    className="w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  />
                  <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                    The folder on the partner server where files will be moved after pickup.
                  </small>
                </div>
              )}
            </div>
          </div>
        )}

        {/* T091: Trigger Type Section - Only show for Remote Drop tasks */}
        {isRemoteDropTask() && (
          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5 border-l-[3px] border-l-primary-500">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-2">Delivery Trigger</h4>
            <p className="m-0 mb-4 text-neutral-500 dark:text-neutral-400 text-sm">
              Choose when files should be delivered to the partner server.
            </p>
            <div className="flex flex-col gap-3">
              {deliveryModeOptions.map((type) => (
                <label
                  key={type}
                  className={`
                    flex items-start gap-3 p-3 cursor-pointer
                    bg-white dark:bg-neutral-900 border rounded-md transition-all duration-200
                    ${deliveryMode === type
                      ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10'
                      : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500'
                    }
                  `}
                >
                  <input type="radio" name="deliveryMode" value={type} checked={deliveryMode === type} onChange={e => setDeliveryMode(e.target.value as DeliveryMode)} className="mt-1 w-4 h-4 accent-primary-500" />
                  <span className="flex flex-col gap-1">
                    <strong className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </strong>
                    <small className="text-neutral-500 dark:text-neutral-400 text-[13px]">
                      {type === 'immediate' && 'Deliver as soon as file is ready in the internal bucket'}
                      {type === 'scheduled' && 'Batch and deliver on a schedule (EventBridge cron/rate)'}
                    </small>
                  </span>
                </label>
              ))}
            </div>

            {/* Delivery schedule expression input (when scheduled) */}
            {deliveryMode === 'scheduled' && (
              <div className="mt-4">
                <label htmlFor="deliverySchedule" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Delivery Schedule Expression
                </label>
                <input
                  id="deliverySchedule"
                  type="text"
                  value={deliverySchedule}
                  onChange={e => setDeliverySchedule(e.target.value)}
                  placeholder="rate(1 hour) or cron(0 8 * * ? *)"
                  className="mt-1 block w-full rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 py-2 text-sm font-mono text-neutral-900 dark:text-neutral-100 focus:border-primary-500 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  EventBridge cron or rate expression. Example: <code>rate(1 hour)</code>, <code>cron(0 8 * * ? *)</code>
                </p>
              </div>
            )}
          </div>
        )}

        {/* 019: HTTP Outbound Configuration - Only show for HTTP API outbound tasks */}
        {isHttpOutboundTask() && (
          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5 border-l-[3px] border-l-primary-500">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-2">HTTP Outbound Configuration</h4>
            <p className="m-0 mb-4 text-neutral-500 dark:text-neutral-400 text-sm">
              Configure how files are delivered via HTTP API.
            </p>
            <div className="space-y-4">
              {/* Delivery Mode */}
              <div className="space-y-2">
                <label htmlFor="httpDeliveryMode" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Delivery Mode
                </label>
                <select
                  id="httpDeliveryMode"
                  value={httpDeliveryMode}
                  onChange={e => setHttpDeliveryMode(e.target.value as 'standard' | 'with_metadata')}
                  className="w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="standard">Standard - File only</option>
                  <option value="with_metadata">With Metadata - File + metadata fields</option>
                </select>
                <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                  Standard mode sends the file as the request body. With Metadata mode includes additional fields.
                </small>
              </div>

              {/* Resource Path */}
              <div className="space-y-2">
                <label htmlFor="httpResourcePath" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Resource Path
                </label>
                <input
                  id="httpResourcePath"
                  type="text"
                  value={httpResourcePath}
                  onChange={e => setHttpResourcePath(e.target.value)}
                  placeholder="/upload or /api/files"
                  className="w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                />
                <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                  Optional path appended to the connection base URL. Leave empty to use the base URL.
                </small>
              </div>

              {/* Content Type Override */}
              <div className="space-y-2">
                <label htmlFor="httpContentTypeOverride" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Content Type Override
                </label>
                <select
                  id="httpContentTypeOverride"
                  value={httpContentTypeOverride}
                  onChange={e => setHttpContentTypeOverride(e.target.value)}
                  className="w-full px-3 py-2.5 border rounded-md text-sm transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="">Use connection default</option>
                  <option value="multipart/form-data">multipart/form-data</option>
                  <option value="application/octet-stream">application/octet-stream</option>
                  <option value="application/json">application/json</option>
                </select>
                <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                  Override the Content-Type header for this task. Leave as default to use the connection setting.
                </small>
              </div>

              {/* Base64 Encoding */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700 dark:text-neutral-300">
                  <input
                    type="checkbox"
                    checked={enableBase64}
                    onChange={e => setEnableBase64(e.target.checked)}
                    className="w-4 h-4 accent-primary-500"
                  />
                  <span>Enable Base64 Encoding</span>
                </label>
                <small className="text-neutral-500 dark:text-neutral-400 text-xs block ml-6">
                  Encode file content as Base64 before sending. Useful for binary files or JSON payloads.
                </small>
              </div>

              {/* T043: With Metadata mode fields */}
              {httpDeliveryMode === 'with_metadata' && (
                <>
                  {/* Request Body Template */}
                  <div className="space-y-2 pt-4 border-t border-neutral-200 dark:border-neutral-600">
                    <label htmlFor="requestBodyTemplate" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      Request Body Template
                    </label>
                    <textarea
                      id="requestBodyTemplate"
                      value={requestBodyTemplate}
                      onChange={e => setRequestBodyTemplate(e.target.value)}
                      rows={6}
                      placeholder={'{\n  "filename": "{{filename}}",\n  "content": "{{file_content}}",\n  "metadata": {\n    "field1": "{{field1}}"\n  }\n}'}
                      className="w-full px-3 py-2.5 border rounded-md text-sm font-mono transition-all duration-200 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    />
                    <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                      JSON template with {'{{variable}}'} placeholders. Use {'{{file_content}}'} for base64-encoded file.
                    </small>
                  </div>

                  {/* Metadata Fields */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      Metadata Fields
                    </label>
                    <div className="space-y-2">
                      {metadataFields.map((field, index) => (
                        <div key={index} className="flex gap-2 items-start bg-white dark:bg-neutral-900 p-3 rounded border border-neutral-200 dark:border-neutral-600">
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <input
                              type="text"
                              value={field.name}
                              onChange={e => updateMetadataField(index, 'name', e.target.value)}
                              placeholder="Field name"
                              className="px-3 py-2 border rounded text-sm bg-neutral-50 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500"
                            />
                            <input
                              type="text"
                              value={field.description}
                              onChange={e => updateMetadataField(index, 'description', e.target.value)}
                              placeholder="Description"
                              className="px-3 py-2 border rounded text-sm bg-neutral-50 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 border-neutral-300 dark:border-neutral-600 focus:outline-none focus:border-primary-500"
                            />
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700 dark:text-neutral-300">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={e => updateMetadataField(index, 'required', e.target.checked)}
                                className="w-4 h-4 accent-primary-500"
                              />
                              <span>Required</span>
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMetadataField(index)}
                            className="px-3 py-2 text-sm text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addMetadataField}
                        className="w-full px-3 py-2 text-sm border border-dashed border-neutral-300 dark:border-neutral-600 rounded text-neutral-600 dark:text-neutral-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      >
                        + Add Field
                      </button>
                    </div>
                    <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                      Define metadata fields that can be provided when triggering transfers via API.
                    </small>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* T050: HTTP Inbound Configuration - Only show for HTTP API inbound tasks */}
        {isHttpInboundTask() && (
          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-4">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-2">HTTP Inbound Configuration</h4>

            {/* T052: Response mode selector */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Response Mode
              </label>
              <select
                value={httpResponseMode}
                onChange={e => setHttpResponseMode(e.target.value as 'standard' | 'presigned_url')}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm"
              >
                <option value="standard">Standard — Response body is the file</option>
                <option value="presigned_url">Presigned URL — Response is a JSON array of download URLs</option>
              </select>
              <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                {httpResponseMode === 'presigned_url'
                  ? 'Partner API returns a JSON array of base64-encoded presigned URLs. Each URL is downloaded separately.'
                  : 'Partner API response body is saved directly as a file.'}
              </small>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Resource Path <span className="text-neutral-400">(optional)</span>
              </label>
              <input
                type="text"
                value={httpResourcePath}
                onChange={e => setHttpResourcePath(e.target.value)}
                placeholder="/api/v1/files/latest"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm"
              />
              <small className="text-neutral-500 dark:text-neutral-400 text-xs">
                Appended to the connection's base URL. Leave empty to use base URL directly.
              </small>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderReview = () => {
    const selectedPartner = partners.find(
      partner => partner.id === formData.partnerId
    );
    // T088: Use transferDirectionOptions from component scope (based on selected connection)
    const selectedDirectionOption = transferDirectionOptions.find(
      o => o.value === formData.transferDirection
    );
    const selectedConnectionForReview = availableConnections.find(
      conn => conn.connectionId === formData.connectionId
    );

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-5 pb-2 border-b border-neutral-200 dark:border-neutral-700">
          Review & Confirm
        </h3>
        <div className="space-y-6">
          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Task Name</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{formData.name}</span>
              </div>
              {/* THUB-160: show the optional display label in review when provided */}
              {formData.description.trim() && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Display Name</label>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{formData.description.trim()}</span>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Partner</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{selectedPartner?.name}</span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Connection</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">
                  {selectedConnectionForReview
                    ? `${selectedConnectionForReview.name} (${selectedConnectionForReview.hostingType} / ${selectedConnectionForReview.transferMethod.toUpperCase()})`
                    : 'No connection selected'}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Transfer Direction</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{selectedDirectionOption?.label || formData.transferDirection}</span>
              </div>
            </div>
          </div>

          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">Schedule</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">
                  {schedule.type.charAt(0).toUpperCase() + schedule.type.slice(1)}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Expression</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100 font-mono">{schedule.expression || 'Manual execution'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Timezone</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{schedule.timezone}</span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Enabled</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{schedule.enabled ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5">
            <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">Configuration</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {getRelevantConfigEntries(
                configuration,
                selectedHostingType,
                formData.transferDirection || undefined
              ).map(([key, value]) => (
                <div key={key} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    {configFieldLabel(key)}
                  </label>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">
                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                  </span>
                </div>
              ))}
              {/* FR-010: Show archive access status for hosted connections */}
              {isHostedConnection() && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Archive Access</label>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{archiveAccessEnabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              )}
              {/* 021: PGP encryption status */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">PGP Encryption</label>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{pgpEnabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
          </div>

          {/* T090: Show post-pickup action for Remote Pickup tasks */}
          {isRemotePickupTask() && (
            <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5 border-l-[3px] border-l-primary-500">
              <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">Post-Pickup Action</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Action</label>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">
                    {postPickupAction === 'none' && 'None - Leave file on partner server'}
                    {postPickupAction === 'delete' && 'Delete - Remove file after pickup'}
                    {postPickupAction === 'move' && 'Move - Move file to archive folder'}
                  </span>
                </div>
                {postPickupAction === 'move' && postPickupTargetPath && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Archive Path</label>
                    <span className="text-sm text-neutral-900 dark:text-neutral-100 font-mono">{postPickupTargetPath}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* T091: Show trigger type for Remote Drop tasks */}
          {isRemoteDropTask() && (
            <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5 border-l-[3px] border-l-primary-500">
              <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">Delivery Trigger</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Trigger Type</label>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">
                    {deliveryMode === 'immediate' && 'Immediate - Deliver as soon as file is ready'}
                    {deliveryMode === 'scheduled' && 'Scheduled - Batch deliver on schedule'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 019: Show HTTP configuration for HTTP Outbound tasks */}
          {isHttpOutboundTask() && (
            <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-md p-5 border-l-[3px] border-l-primary-500">
              <h4 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 m-0 mb-4">HTTP Outbound Configuration</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Delivery Mode</label>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">
                    {httpDeliveryMode === 'standard' ? 'Standard - File only' : 'With Metadata - File + metadata fields'}
                  </span>
                </div>
                {httpResourcePath && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Resource Path</label>
                    <span className="text-sm text-neutral-900 dark:text-neutral-100 font-mono">{httpResourcePath}</span>
                  </div>
                )}
                {httpContentTypeOverride && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Content Type</label>
                    <span className="text-sm text-neutral-900 dark:text-neutral-100">{httpContentTypeOverride}</span>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Base64 Encoding</label>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{enableBase64 ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 'basic':
        return renderBasicInfo();
      case 'schedule':
        return renderSchedule();
      case 'configuration':
        return renderConfiguration();
      case 'review':
        return renderReview();
      default:
        return renderBasicInfo();
    }
  };

  const getVisibleSteps = () => {
    const hasScheduling = requiresScheduling();
    const baseSteps = ['basic', 'configuration', 'review'];
    const baseLabels = ['Basic Info', 'Configuration', 'Review'];

    if (hasScheduling) {
      return {
        steps: ['basic', 'schedule', 'configuration', 'review'],
        labels: ['Basic Info', 'Schedule', 'Configuration', 'Review'],
      };
    }

    return {
      steps: baseSteps,
      labels: baseLabels,
    };
  };

  const { steps: visibleSteps, labels: visibleLabels } = getVisibleSteps();
  const currentStepIndex = visibleSteps.indexOf(currentStep);

  return (
    <div className={`
      bg-white dark:bg-slate-900
      border border-neutral-200 dark:border-slate-700
      rounded-lg shadow-lg
      max-w-[800px] w-full max-h-[90vh] overflow-hidden flex flex-col
      ${className}
    `}>
      <div className="
        px-6 py-4
        bg-neutral-50 dark:bg-slate-800
        border-b border-neutral-200 dark:border-slate-700
      ">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 m-0 mb-4">
          Create New Task
        </h2>
        <div className="flex gap-1">
          {visibleLabels.map((label, index) => (
            <div
              key={label}
              className={`
                flex items-center gap-2 px-3 py-2 rounded
                transition-colors duration-200
                ${index <= currentStepIndex
                  ? 'bg-primary-500 text-white'
                  : 'bg-neutral-200 dark:bg-slate-700 text-neutral-500 dark:text-neutral-400'
                }
                ${index === currentStepIndex ? 'ring-2 ring-primary-300 dark:ring-primary-700' : ''}
              `}
            >
              <span className={`
                inline-flex items-center justify-center w-6 h-6 text-sm font-semibold rounded-full
                ${index <= currentStepIndex
                  ? 'bg-white/20 text-white'
                  : 'bg-neutral-100 dark:bg-slate-600 text-neutral-500 dark:text-neutral-400'
                }
              `}>
                {index + 1}
              </span>
              <span className="text-sm font-medium hidden sm:inline">{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">{getStepContent()}</div>

      <div className="
        flex gap-4 justify-end
        px-6 py-4
        border-t border-neutral-200 dark:border-slate-700
        bg-neutral-50 dark:bg-slate-800
      ">
        <button
          type="button"
          onClick={onCancel}
          className="
            px-6 py-2.5
            bg-neutral-100 dark:bg-slate-700
            border border-neutral-300 dark:border-slate-600
            text-neutral-700 dark:text-neutral-200
            rounded-md text-sm font-medium cursor-pointer
            transition-colors duration-200
            hover:bg-neutral-200 dark:hover:bg-slate-600
          "
        >
          Cancel
        </button>

        {currentStep !== 'basic' && (
          <button
            type="button"
            onClick={handlePrevious}
            className="
              px-6 py-2.5
              bg-neutral-100 dark:bg-slate-700
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-neutral-200
              rounded-md text-sm font-medium cursor-pointer
              transition-colors duration-200
              hover:bg-neutral-200 dark:hover:bg-slate-600
            "
          >
            Previous
          </button>
        )}

        <button
          type="button"
          onClick={handleNext}
          className="
            px-6 py-2.5
            bg-primary-500 hover:bg-primary-600
            border-none text-white
            rounded-md text-sm font-medium cursor-pointer
            transition-colors duration-200
            disabled:opacity-60 disabled:cursor-not-allowed
          "
          disabled={isValidating}
        >
          {isValidating
            ? 'Validating...'
            : currentStep === 'review'
              ? 'Create Task'
              : 'Next'}
        </button>
      </div>
    </div>
  );
}
